import { useQuery } from '@tanstack/react-query'

interface GeoResult {
  country: string
  inConsentRegion: boolean
}

// Explicit allowlist of IANA timezones that correspond to EEA/UK/CH countries.
// Using an allowlist rather than `Europe/*` prefix to avoid false positives for
// non-consent European countries (Russia, Turkey, Ukraine, Belarus, etc.).
// Cyprus (CY) intentionally uses Asia/Nicosia; Portugal + Spain have Atlantic zones.
const EEA_TIMEZONES = new Set([
  'Europe/Vienna',       // AT
  'Europe/Brussels',     // BE
  'Europe/Sofia',        // BG
  'Europe/Zagreb',       // HR
  'Asia/Nicosia',        // CY — Cyprus uses Asia/ prefix
  'Europe/Prague',       // CZ
  'Europe/Copenhagen',   // DK
  'Europe/Tallinn',      // EE
  'Europe/Helsinki',     // FI
  'Europe/Paris',        // FR
  'Europe/Berlin',       // DE
  'Europe/Busingen',     // DE (exclave)
  'Europe/Athens',       // GR
  'Europe/Budapest',     // HU
  'Europe/Dublin',       // IE
  'Europe/Rome',         // IT
  'Europe/Riga',         // LV
  'Europe/Vilnius',      // LT
  'Europe/Luxembourg',   // LU
  'Europe/Malta',        // MT
  'Europe/Amsterdam',    // NL
  'Europe/Warsaw',       // PL
  'Europe/Lisbon',       // PT
  'Atlantic/Azores',     // PT (Azores)
  'Atlantic/Madeira',    // PT (Madeira)
  'Europe/Bucharest',    // RO
  'Europe/Bratislava',   // SK
  'Europe/Ljubljana',    // SI
  'Europe/Madrid',       // ES
  'Atlantic/Canary',     // ES (Canary Islands)
  'Africa/Ceuta',        // ES (Ceuta)
  'Europe/Stockholm',    // SE
  'Europe/London',       // GB
  'Europe/Zurich',       // CH
  'Atlantic/Reykjavik',  // IS (Iceland)
  'Europe/Vaduz',        // LI (Liechtenstein)
  'Europe/Oslo',         // NO
])

const SESSION_KEY = 'mori.geo'

function timezoneConsentFallback(): GeoResult {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return { country: 'XX', inConsentRegion: EEA_TIMEZONES.has(tz) }
}

async function fetchGeo(): Promise<GeoResult> {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (raw) return JSON.parse(raw) as GeoResult
  } catch { /* ignore parse or storage errors */ }

  try {
    const res = await fetch('/api/geo')
    if (!res.ok) throw new Error(`geo: ${res.status}`)
    // Guard against SPA rewrite returning HTML instead of JSON (local Vite dev)
    const text = await res.text()
    const data = JSON.parse(text) as GeoResult
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data))
    } catch { /* storage quota or privacy mode — non-fatal */ }
    return data
  } catch {
    // API unavailable (local dev, network error) — fall back to timezone heuristic.
    // Result is NOT cached in sessionStorage so a future navigation retries the API.
    return timezoneConsentFallback()
  }
}

export function useGeo() {
  const query = useQuery<GeoResult>({
    queryKey: ['geo'],
    queryFn: fetchGeo,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24,
    retry: false,
  })

  return {
    country: query.data?.country ?? 'XX',
    inConsentRegion: query.data?.inConsentRegion ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
